import { useState, useEffect, useCallback, useRef } from 'react';
import type { FlatItem } from './types';
import { fromFlatItems } from './types';
import type { LineItemData } from './LineItemList';
import type { Rates, JobBreakdown, BreakdownDocumentContents } from '@jobos/shared';
import { buildDocContents, type DocLineItemInput } from './buildDocContents';
import { useSaveBreakdown, usePatchBreakdown } from '@/hooks/useBreakdown';
import { useWriteBreakdownDocumentContents } from '@/hooks/useBreakdownDocuments';
import { saveOffline, loadOffline, clearOffline, offlineBreakdownKey } from '@/hooks/useOfflineStorage';
import type { useUndoHistory } from './useUndoHistory';

const AUTOSAVE_DELAY_MS = 60_000;
const LOCAL_BACKUP_DELAY_MS = 2_000;

export interface UseBreakdownSaveOptions {
  jobId: string;
  flatItems: FlatItem[];
  jobRates: Rates;
  roundingPrecision: number | null;
  initFlatItems: (items: FlatItem[] | JobBreakdown) => void;
  undoHistory: ReturnType<typeof useUndoHistory>;
  isPreviewMode: boolean;
  setSelectedItemId: (id: string | null) => void;
  setJobRates: (rates: Rates) => void;
  markEdited: () => void;
  initialLoadCompleteRef: React.MutableRefObject<boolean>;
  // When provided (BREAKDOWN_DOCS_ENABLED flag-on path), saves target the
  // document-contents endpoint PUT /jobs/:jobId/breakdown/documents/:docId/contents
  // instead of the legacy PUT /jobs/:jobId/breakdown. The id is captured into
  // a ref at markDirty time so a switch to a different doc mid-debounce sends
  // the in-flight payload to the *original* doc, not the new one.
  documentId?: string;
}

export interface UseBreakdownSaveReturn {
  isDirty: boolean;
  isSaving: boolean;
  hasOfflineEdits: boolean;
  saveError: string | null;
  save: () => Promise<void>;
  /**
   * Drain any pending breakdown save and reject if a fresh save error surfaces
   * during this call. Used by the JobDetail flush context so a slot-switch
   * can wait for in-flight breakdown saves before swapping live tables.
   */
  flushPendingSave: () => Promise<void>;
  markDirty: (skipHistory?: boolean) => void;
  downloadBackup: (jobCode: string) => void;
  patchBreakdown: ReturnType<typeof usePatchBreakdown>;
  pendingRecovery: { flatItems: FlatItem[]; jobRates: Rates } | null;
  acceptRecovery: () => void;
  discardRecovery: () => void;
}

export function useBreakdownSave({
  jobId,
  flatItems,
  jobRates,
  roundingPrecision,
  initFlatItems,
  undoHistory,
  isPreviewMode,
  setSelectedItemId,
  setJobRates,
  markEdited,
  initialLoadCompleteRef,
  documentId,
}: UseBreakdownSaveOptions): UseBreakdownSaveReturn {
  const saveBreakdown = useSaveBreakdown(jobId);
  const patchBreakdown = usePatchBreakdown(jobId);
  const writeDocContents = useWriteBreakdownDocumentContents(jobId);

  const [isDirty, setIsDirty] = useState(false);
  const [hasOfflineEdits, setHasOfflineEdits] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingRecovery, setPendingRecovery] = useState<{ flatItems: FlatItem[]; jobRates: Rates } | null>(null);

  // Refs for latest values (used in save to avoid stale closures)
  const flatItemsRef = useRef(flatItems);
  flatItemsRef.current = flatItems;
  const jobRatesRef = useRef(jobRates);
  jobRatesRef.current = jobRates;
  const roundingPrecisionRef = useRef(roundingPrecision);
  roundingPrecisionRef.current = roundingPrecision;
  // Current prop value of the open document id. Updated every render.
  const currentDocumentIdRef = useRef<string | undefined>(documentId);
  currentDocumentIdRef.current = documentId;
  // Captured at the moment markDirty fired — the autosave timer and
  // performSave read from THIS ref, not the prop ref. A mid-debounce switch
  // to a different doc therefore sends the in-flight payload to the original
  // doc; the new doc starts clean from its own dirty cycle.
  const capturedDocumentIdRef = useRef<string | undefined>(undefined);

  // Dirty ref for autosave timer to check
  const isDirtyRef = useRef(false);

  // Autosave and in-flight guard refs
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saveInFlightRef = useRef(false);
  // Promise-form of the in-flight guard so external callers (flushPendingSave)
  // can join the existing save instead of starting a duplicate one.
  const saveInFlightPromiseRef = useRef<Promise<void> | null>(null);
  // Mirrors of saveError + isDirty so flushPendingSave can read latest values
  // from inside its useCallback without retriggering on every state change.
  const saveErrorRef = useRef<string | null>(null);

  // Local backup debounce ref
  const localBackupTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAutosaveTimer = useCallback(() => {
    if (autosaveTimerRef.current !== null) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
  }, []);

  // Serialize flatItems into the PUT payload shape
  const buildPayload = useCallback((items: FlatItem[], rates: Rates, rounding: number | null) => {
    const { looseItems, projects } = fromFlatItems(items);

    const serializeSubItems = (subItems: LineItemData['subItems']) =>
      subItems.map((sub, i) => ({
        id: sub.id,
        type: sub.type,
        hours: 'hours' in sub ? (sub as any).hours : undefined,
        rate: 'rate' in sub ? (sub as any).rate : undefined,
        rateType: 'rateType' in sub ? (sub as any).rateType : undefined,
        amount: 'amount' in sub ? (sub as any).amount : undefined,
        note: sub.note,
        markupRate: (sub as any).markupRate,
        sortOrder: i,
      }));

    const serializeLineItem = (item: LineItemData, projectId: string | null) => ({
      id: item.id,
      category: item.category,
      description: item.description || undefined,
      sortOrder: item.sortOrder ?? 0,
      templateId: item.templateId,
      itemDate: item.itemDate,
      projectId,
      subItems: serializeSubItems(item.subItems),
    });

    const allLineItems = [
      ...looseItems.map(item => serializeLineItem(item, null)),
      ...projects.flatMap(project =>
        project.lineItems.map(item => serializeLineItem(item, project.id))
      ),
    ];

    return {
      lineItems: allLineItems,
      taxRate: rates.taxRate,
      markupRate: rates.markupRate,
      roundingPrecision: rounding,
    };
  }, []);

  // Build the document-contents payload shape (BreakdownDocumentContents).
  // Differs from buildPayload in that the projects array is sent explicitly
  // and rates flatten at the root with per-document semantics.
  const buildDocContentsPayload = useCallback(
    (items: FlatItem[], rates: Rates, rounding: number | null): BreakdownDocumentContents => {
      const { looseItems, projects } = fromFlatItems(items);

      const toDocLineItem = (item: LineItemData, projectId: string | null): DocLineItemInput => ({
        id: item.id,
        category: item.category,
        description: item.description ?? null,
        sortOrder: item.sortOrder ?? 0,
        templateId: item.templateId ?? null,
        itemDate: item.itemDate ?? null,
        projectId,
        subItems: item.subItems.map((sub) => ({
          id: sub.id,
          type: sub.type,
          hours: 'hours' in sub ? (sub as any).hours ?? null : null,
          rate: 'rate' in sub ? (sub as any).rate ?? null : null,
          rateType: 'rateType' in sub ? (sub as any).rateType ?? null : null,
          amount: 'amount' in sub ? (sub as any).amount ?? null : null,
          note: sub.note ?? null,
          markupRate: (sub as any).markupRate ?? null,
        })),
      });

      return buildDocContents({
        taxRate: rates.taxRate,
        markupRate: rates.markupRate,
        roundingPrecision: rounding,
        projects: projects.map((p) => ({ id: p.id, name: p.name, sortOrder: p.sortOrder ?? 0 })),
        lineItems: [
          ...looseItems.map((item) => toDocLineItem(item, null)),
          ...projects.flatMap((project) =>
            project.lineItems.map((item) => toDocLineItem(item, project.id)),
          ),
        ],
      });
    },
    [],
  );

  // Core save logic with in-flight guard
  const performSave = useCallback(async () => {
    const currentFlatItems = flatItemsRef.current;
    const currentRates = jobRatesRef.current;
    const currentRounding = roundingPrecisionRef.current;
    // Read captured docId — falls back to the current prop when no markDirty
    // ever fired (e.g. the wrapper invoked save() directly, like a "Save now" button).
    const targetDocId = capturedDocumentIdRef.current ?? currentDocumentIdRef.current;

    // If offline, save to localStorage
    if (!navigator.onLine) {
      saveOffline(offlineBreakdownKey(jobId, targetDocId), { flatItems: currentFlatItems, jobRates: currentRates });
      setHasOfflineEdits(true);
      setIsDirty(false);
      isDirtyRef.current = false;
      setSaveError(null);
      return;
    }

    saveInFlightRef.current = true;
    try {
      if (targetDocId) {
        // Flag-on path: send the doc-contents shape to the per-document endpoint.
        const docPayload = buildDocContentsPayload(currentFlatItems, currentRates, currentRounding);
        await writeDocContents.mutateAsync({ docId: targetDocId, contents: docPayload });
        // The mutation invalidates the doc detail query, which will repopulate
        // the editor via the wrapper's useBreakdownDocument hook. We don't
        // need to call initFlatItems here — the doc endpoint returns only
        // {success: true} and the canonical totals come from the re-fetch.
      } else {
        // Legacy path: existing PUT /breakdown.
        const payload = buildPayload(currentFlatItems, currentRates, currentRounding);
        const response = await saveBreakdown.mutateAsync(payload);
        initFlatItems(response);
        if (response.taxRate !== undefined && response.markupRate !== undefined) {
          setJobRates({ taxRate: response.taxRate, markupRate: response.markupRate });
        }
      }

      markEdited();
      clearOffline(offlineBreakdownKey(jobId, targetDocId));
      setHasOfflineEdits(false);
      setIsDirty(false);
      isDirtyRef.current = false;
      // Clear the captured docId now that the save it was bound to has resolved.
      capturedDocumentIdRef.current = undefined;
      setSaveError(null);
      saveErrorRef.current = null;

      // Clear local backup timer on successful save
      if (localBackupTimerRef.current !== null) {
        clearTimeout(localBackupTimerRef.current);
        localBackupTimerRef.current = null;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setSaveError(message);
      // Mirror to ref synchronously so flushPendingSave (which reads the ref
      // immediately after awaiting save()) sees the new error before the
      // useEffect/state-sync has a chance to run.
      saveErrorRef.current = message;
      // Also persist to localStorage as safety net
      saveOffline(offlineBreakdownKey(jobId, targetDocId), { flatItems: currentFlatItems, jobRates: currentRates });
    } finally {
      saveInFlightRef.current = false;
    }
  }, [
    jobId,
    saveBreakdown,
    writeDocContents,
    markEdited,
    buildPayload,
    buildDocContentsPayload,
    initFlatItems,
    setJobRates,
  ]);

  // Schedule autosave with 60s debounce
  const scheduleAutosave = useCallback(() => {
    clearAutosaveTimer();
    autosaveTimerRef.current = setTimeout(() => {
      // Skip if preview mode or offline
      if (isPreviewMode || !navigator.onLine) return;

      // If a save is already in flight, reschedule
      if (saveInFlightRef.current) {
        scheduleAutosave();
        return;
      }

      // Only save if still dirty
      if (isDirtyRef.current) {
        performSave();
      }
    }, AUTOSAVE_DELAY_MS);
  }, [clearAutosaveTimer, isPreviewMode, performSave]);

  // Persist current state to localStorage with 2s debounce
  const persistToLocalStorage = useCallback(() => {
    if (localBackupTimerRef.current !== null) {
      clearTimeout(localBackupTimerRef.current);
    }
    localBackupTimerRef.current = setTimeout(() => {
      const currentFlatItems = flatItemsRef.current;
      const currentRates = jobRatesRef.current;
      saveOffline(offlineBreakdownKey(jobId, currentDocumentIdRef.current), { flatItems: currentFlatItems, jobRates: currentRates });
    }, LOCAL_BACKUP_DELAY_MS);
  }, [jobId]);

  // Save: clear autosave timer then perform save. Tracks the in-flight promise
  // so flushPendingSave can join an existing save instead of starting a new one.
  const save = useCallback(async (): Promise<void> => {
    if (saveInFlightPromiseRef.current) return saveInFlightPromiseRef.current;
    clearAutosaveTimer();
    const promise = (async () => {
      try {
        await performSave();
      } finally {
        saveInFlightPromiseRef.current = null;
      }
    })();
    saveInFlightPromiseRef.current = promise;
    return promise;
  }, [clearAutosaveTimer, performSave]);

  // Keep refs in sync for flushPendingSave's closure.
  useEffect(() => {
    saveErrorRef.current = saveError;
  }, [saveError]);

  // Drain any pending save, then surface a fresh save error as a rejection.
  // priorError baseline avoids spuriously rejecting on a stale error that was
  // already present before this call (e.g. a previously failed save the user
  // chose to ignore).
  const flushPendingSave = useCallback(async (): Promise<void> => {
    const priorError = saveErrorRef.current;
    if (saveInFlightPromiseRef.current) {
      await saveInFlightPromiseRef.current;
    } else if (isDirtyRef.current) {
      await save();
    }
    if (saveErrorRef.current && saveErrorRef.current !== priorError) {
      throw new Error(`Save failed: ${saveErrorRef.current}`);
    }
  }, [save]);

  // Mark dirty (with optional undo history snapshot)
  const markDirty = useCallback((skipHistory = false) => {
    if (!skipHistory && !isPreviewMode) {
      undoHistory.markDirty({ flatItems, jobRates, roundingPrecision });
    }
    setIsDirty(true);
    isDirtyRef.current = true;
    // Snapshot the *current* documentId — if the wrapper switches to a
    // different doc before the debounced save fires, the captured id wins
    // and the in-flight payload still flows to the doc it originated from.
    // Only overwrite when no save is already in flight, otherwise the
    // previous capture must complete its bound write first.
    if (capturedDocumentIdRef.current === undefined) {
      capturedDocumentIdRef.current = currentDocumentIdRef.current;
    }
    scheduleAutosave();
    persistToLocalStorage();
  }, [undoHistory, jobRates, roundingPrecision, isPreviewMode, flatItems, scheduleAutosave, persistToLocalStorage]);

  // Download current breakdown state as a JSON backup file
  const downloadBackup = useCallback((jobCode: string) => {
    const currentFlatItems = flatItemsRef.current;
    const currentRates = jobRatesRef.current;
    const currentRounding = roundingPrecisionRef.current;

    const backup = {
      version: 1,
      exportedAt: new Date().toISOString(),
      jobId,
      jobCode,
      flatItems: currentFlatItems,
      jobRates: currentRates,
      roundingPrecision: currentRounding,
    };

    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `breakdown-backup-${jobCode}-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [jobId]);

  // Accept recovery: hydrate from pending recovery data
  const acceptRecovery = useCallback(() => {
    if (!pendingRecovery) return;
    initFlatItems(pendingRecovery.flatItems);
    setJobRates(pendingRecovery.jobRates);
    // Update refs immediately so performSave reads the recovered data
    // (React state won't have re-rendered yet)
    flatItemsRef.current = pendingRecovery.flatItems;
    jobRatesRef.current = pendingRecovery.jobRates;
    const firstItem = pendingRecovery.flatItems.find(i => i.type === 'line-item');
    if (firstItem) setSelectedItemId(firstItem.id);
    setPendingRecovery(null);

    if (navigator.onLine) {
      // Auto-save immediately — the user already chose to restore
      performSave();
    } else {
      setHasOfflineEdits(true);
      setIsDirty(true);
      isDirtyRef.current = true;
    }
  }, [pendingRecovery, initFlatItems, setJobRates, setSelectedItemId, performSave]);

  // Discard recovery: clear localStorage and dismiss
  const discardRecovery = useCallback(() => {
    clearOffline(offlineBreakdownKey(jobId, currentDocumentIdRef.current));
    setPendingRecovery(null);
  }, [jobId]);

  // Sync offline edits when back online
  useEffect(() => {
    const handleOnline = () => {
      if (hasOfflineEdits && initialLoadCompleteRef.current) {
        save();
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [hasOfflineEdits, save]);

  // Detect recoverable edits from localStorage on mount. Scoped to the open
  // document so we never surface another document's backup for recovery (which
  // would restore its rows into this doc and corrupt it on save).
  useEffect(() => {
    if (!initialLoadCompleteRef.current) return;
    const offlineData = loadOffline<{ flatItems: FlatItem[]; jobRates: Rates }>(
      offlineBreakdownKey(jobId, documentId),
    );
    if (offlineData) {
      setPendingRecovery(offlineData);
    }
  }, [jobId, documentId, initialLoadCompleteRef.current]);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current !== null) {
        clearTimeout(autosaveTimerRef.current);
      }
      if (localBackupTimerRef.current !== null) {
        clearTimeout(localBackupTimerRef.current);
      }
    };
  }, []);

  return {
    isDirty,
    // Reflect either mutation — flag-on autosave uses writeDocContents, flag-off
    // uses the legacy saveBreakdown. Without both the "Saving..." indicator
    // never shows on word-processor docs.
    isSaving: saveBreakdown.isPending || writeDocContents.isPending,
    hasOfflineEdits,
    saveError,
    save,
    flushPendingSave,
    markDirty,
    downloadBackup,
    patchBreakdown,
    pendingRecovery,
    acceptRecovery,
    discardRecovery,
  };
}
